/**
 * Enrolment.
 *
 * Replaces `license.tsx`, which asked an operator to type a `licenseKey` that
 * was simultaneously the branch identity and 1C's `stock_location_code`, and
 * which had to be written atomically beside two QPay codes or — in
 * `routers/settings.ts`'s own words — "the stores drift apart". The kiosk trades
 * a one-time code for a token and pulls the rest.
 */
import { useState } from 'react'
import { Button, Card, Notice } from '@/components/ui'
import { refusalOf, setToken, trpc } from '@/api/client'
import { useT } from '@/i18n'
import { useSession, writeToken } from '@/store/session'
import { inShell } from '@/shell/native'

export function Pair({ onPaired }: { onPaired: () => void }) {
  const t = useT()
  const [code, setCode] = useState('')
  const [name, setName] = useState('Kiosk 1')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const setSessionToken = useSession((s) => s.setToken)

  const enrol = async () => {
    setBusy(true)
    setProblem(null)
    try {
      const result = await trpc.devices.enrol.mutate({
        code: code.trim(),
        deviceName: name.trim(),
        platform: navigator.platform,
        ...(inShell() ? { shellVersion: 'shell' } : {})
      })
      const source = await writeToken(result.token)
      setToken(result.token)
      setSessionToken(result.token, source)
      onPaired()
    } catch (error) {
      setProblem(refusalOf(error) ?? t('common.error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex h-full items-center justify-center p-10">
      <Card className="w-full max-w-xl space-y-6 p-8">
        <h1 className="text-[length:var(--text-kiosk-xl)] font-bold">{t('boot.notEnrolled')}</h1>

        <label className="block space-y-2">
          <span className="font-semibold">{t('settings.device')}</span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value.slice(0, 40))}
            className="w-full rounded-[var(--radius-card)] border border-[var(--color-line)] px-4 py-3"
          />
        </label>

        {/*
          The placeholder is the only hint a technician standing at a kiosk
          gets, and it said `branch.secret` — the pre-O3 shared secret this
          scheme replaced. That format has not been accepted since enrolment
          codes became rows, so the hint described a code the server would
          always refuse. `services/enrolment.ts` mints `MGC-XXXX-XXXX` from an
          alphabet with no O/0 and no I/1/L, because a code is read aloud down
          a phone and a transcription error costs a site visit.
        */}
        <label className="block space-y-2">
          <span className="font-semibold">{t('pair.code')}</span>
          <input
            value={code}
            onChange={(event) => setCode(event.target.value.trim())}
            placeholder="MGC-XXXX-XXXX"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            className="w-full rounded-[var(--radius-card)] border border-[var(--color-line)] px-4 py-3 font-mono"
          />
        </label>

        {problem !== null && <Notice tone="bad">{problem}</Notice>}

        <Button className="w-full" disabled={code.length < 6 || busy} onClick={() => void enrol()}>
          {busy ? t('common.loading') : t('common.confirm')}
        </Button>
      </Card>
    </div>
  )
}
